
import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ColumnConfig, RowAction } from "./ReusableTablePage";

interface ReusableTableProps {
  data: any[];
  columns: ColumnConfig[];
  rowActions: RowAction[];
  sortBy: { key: string; direction: "asc" | "desc" | null };
  onSortChange: (key: string) => void;
  onRowClick?: (row: any) => void;
  onColumnFilterChange: (key: string, value: any) => void;
  columnFilters: Record<string, any>;
  isLoading: boolean;
}

export const ReusableTable: React.FC<ReusableTableProps> = ({
  data,
  columns,
  rowActions,
  sortBy,
  onSortChange,
  onRowClick,
  onColumnFilterChange,
  columnFilters,
  isLoading,
}) => {
  // Get sort icon based on sort direction
  const getSortIcon = (key: string) => {
    if (!sortBy || sortBy.key !== key || !sortBy.direction) {
      return <span className="ml-1 text-xs text-muted-foreground">↕</span>;
    }
    return sortBy.direction === "asc" ? (
      <span className="ml-1 text-xs">↑</span>
    ) : (
      <span className="ml-1 text-xs">↓</span>
    );
  };

  return (
    <div>
      <ScrollArea className="h-full">
        <div className="min-w-full overflow-x-auto">
          <Table className="border-collapse border-spacing-0 min-w-full">
          <TableHeader>
            <TableRow className="border-b border-border">
              {columns.map((column) => (
                <TableHead
                  key={column.key}
                  className={`${column.sortable ? "cursor-pointer" : ""} ${
                    column.width ? `w-[${column.width}]` : ""
                  } text-left align-middle`}
                  onClick={column.sortable ? () => onSortChange(column.key) : undefined}
                >
                  <div className="flex items-center">
                    <span>{column.header}</span>
                    {column.sortable && getSortIcon(column.key)}
                  </div>
                </TableHead>
              ))}
              {rowActions.length > 0 && (
                <TableHead className="text-right">Actions</TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + (rowActions.length > 0 ? 1 : 0)}
                  className="h-24 text-center"
                >
                  <div className="flex justify-center items-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
                    <span className="ml-2">Loading...</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + (rowActions.length > 0 ? 1 : 0)}
                  className="h-24 text-center"
                >
                  No data available
                </TableCell>
              </TableRow>
            ) : (
              data.map((row, index) => (
                <TableRow
                  key={index}
                  className={`border-b-[1px] border-border/90 ${onRowClick ? "cursor-pointer hover:bg-muted/50" : ""}`}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                  {columns.map((column) => (
                     <TableCell 
                      key={`${index}-${column.key}`}
                      className={`align-middle ${column.key === "actions" ? "text-right" : ""}`}
                      style={{ maxWidth: column.width ? column.width : undefined }}
                    >
                      {column.render
                        ? column.render(row[column.key], row)
                        : row[column.key] !== undefined && row[column.key] !== null
                        ? String(row[column.key])
                        : "-"}
                    </TableCell>
                  ))}
                  {rowActions.length > 0 && (
                    <TableCell className="text-right p-2">
                      <div className="flex justify-end space-x-1">
                        {rowActions
                          .filter((action) => !action.showCondition || action.showCondition(row))
                          .map((action, actionIndex) => (
                            <Button
                              key={actionIndex}
                              variant="ghost"
                              size="icon"
                              className={`h-8 w-8 p-0 ${action.className || ""}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                action.onClick(row);
                              }}
                              title={action.label}
                            >
                              {action.icon && <action.icon className="h-4 w-4" />}
                            </Button>
                          ))}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
          </Table>
        </div>
      </ScrollArea>
    </div>
  );
};
